import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Copy, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MFASetup = ({ user, onBack, onMfaEnabled, enforced = false, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, has_backup_codes: false });
  const [setupData, setSetupData] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [step, setStep] = useState('status'); // status, setup, verify, complete
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState({});

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/mfa/status`);
      setMfaStatus(response.data);
      if (response.data.mfa_enabled) {
        setStep('enabled');
        // If MFA is already enabled, notify parent
        if (onMfaEnabled) {
          onMfaEnabled();
        }
      }
    } catch (error) {
      toast.error('Failed to load MFA status');
    }
  };

  const handleSetupMfa = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/mfa/setup`);
      setSetupData(response.data);
      setStep('setup');
      toast.success('MFA setup initiated. Scan the QR code with your authenticator app.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/mfa/enable`, {
        token: verificationToken
      });
      toast.success('MFA enabled successfully!');
      setStep('complete');
      await loadMfaStatus();
      // Notify parent that MFA is now enabled
      if (onMfaEnabled) {
        onMfaEnabled();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    const token = prompt('Enter your current MFA code to disable MFA:');
    if (!token) return;

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/mfa/disable`, { token });
      toast.success('MFA disabled successfully');
      setStep('status');
      await loadMfaStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const token = prompt('Enter your current MFA code to regenerate backup codes:');
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/mfa/regenerate-backup-codes`, { token });
      setSetupData({ ...setupData, backup_codes: response.data.backup_codes });
      setStep('backup-codes');
      toast.success('Backup codes regenerated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'secret') {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } else {
      setCopiedCodes({ ...copiedCodes, [text]: true });
      setTimeout(() => {
        setCopiedCodes(prev => ({ ...prev, [text]: false }));
      }, 2000);
    }
    toast.success('Copied to clipboard');
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        {!enforced && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        )}
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold">Multi-Factor Authentication (MFA)</h1>
        </div>
        <p className="text-gray-600 mt-2">
          {enforced 
            ? "Your organization requires MFA to be enabled. Please complete the setup to continue."
            : "Add an extra layer of security to your account with TOTP-based authentication"}
        </p>
      </div>

      {/* Status/Main View */}
      {step === 'status' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">MFA Status</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">MFA Protection</p>
                <p className="text-sm text-gray-600">
                  {mfaStatus.mfa_enabled ? 'Your account is protected with MFA' : 'Your account is not protected with MFA'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {mfaStatus.mfa_enabled ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                )}
                <span className={`font-semibold ${mfaStatus.mfa_enabled ? 'text-green-600' : 'text-orange-600'}`}>
                  {mfaStatus.mfa_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            {!mfaStatus.mfa_enabled && (
              <div className="mt-6">
                <button
                  onClick={handleSetupMfa}
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'Setting up...' : 'Enable MFA'}
                </button>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">What you'll need:</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• An authenticator app (Google Authenticator, Authy, Microsoft Authenticator)</li>
                    <li>• Your smartphone to scan the QR code</li>
                    <li>• A secure place to store backup codes</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enabled Status */}
      {step === 'enabled' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 text-green-600 mb-6">
            <CheckCircle className="w-6 h-6" />
            <h2 className="text-xl font-semibold">MFA is Enabled</h2>
          </div>
          <div className="space-y-4">
            <p className="text-gray-600">
              Your account is protected with multi-factor authentication. You'll need to enter a code from your authenticator app each time you log in.
            </p>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleRegenerateBackupCodes}
                disabled={loading}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Regenerate Backup Codes
              </button>
              <button
                onClick={handleDisableMfa}
                disabled={loading}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Disable MFA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup Step */}
      {step === 'setup' && setupData && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Step 1: Scan QR Code</h2>
            <div className="flex flex-col items-center">
              <img src={setupData.qr_code_data} alt="MFA QR Code" className="w-64 h-64 border-2 border-gray-200 rounded-lg" />
              <p className="text-sm text-gray-600 mt-4 text-center">
                Scan this QR code with your authenticator app
              </p>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white border rounded text-sm font-mono">
                  {setupData.secret}
                </code>
                <button
                  onClick={() => copyToClipboard(setupData.secret, 'secret')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  {copiedSecret ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              onClick={() => setStep('verify')}
              className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Continue to Verification
            </button>
          </div>

          {/* Backup Codes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Step 2: Save Backup Codes</h2>
            <p className="text-gray-600 mb-4">
              Store these backup codes in a secure location. Each code can be used once if you lose access to your authenticator app.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {setupData.backup_codes.map((code, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <code className="flex-1 font-mono text-sm">{code}</code>
                  <button
                    onClick={() => copyToClipboard(code, 'code')}
                    className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                  >
                    {copiedCodes[code] ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Verification Step */}
      {step === 'verify' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Step 3: Verify Setup</h2>
          <p className="text-gray-600 mb-6">
            Enter the 6-digit code from your authenticator app to complete setup
          </p>
          <div className="max-w-md mx-auto">
            <input
              type="text"
              value={verificationToken}
              onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full p-4 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              maxLength="6"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setStep('setup')}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Back
              </button>
              <button
                onClick={handleVerifyAndEnable}
                disabled={loading || verificationToken.length !== 6}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Step */}
      {step === 'complete' && (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">MFA Enabled Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Your account is now protected with multi-factor authentication. You'll need to enter a code from your authenticator app each time you log in.
          </p>
          <button
            onClick={onBack}
            className="py-3 px-8 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Return to Dashboard
          </button>
        </div>
      )}

      {/* Backup Codes Display */}
      {step === 'backup-codes' && setupData && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">New Backup Codes</h2>
          <p className="text-gray-600 mb-4">
            Store these new backup codes in a secure location. Your old backup codes are no longer valid.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {setupData.backup_codes.map((code, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <code className="flex-1 font-mono text-sm">{code}</code>
                <button
                  onClick={() => copyToClipboard(code, 'code')}
                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                >
                  {copiedCodes[code] ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setStep('enabled')}
            className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Done
          </button>
        </div>
      )}

      {/* Logout option when MFA is enforced */}
      {enforced && onLogout && (
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500 mb-3">
            Can't set up MFA right now? You can logout and complete this later.
          </p>
          <button
            onClick={onLogout}
            className="px-6 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 font-medium transition-colors"
          >
            Logout for Now
          </button>
        </div>
      )}
    </div>
  );
};

export default MFASetup;
